import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Divider } from "@material-ui/core";
import api from "../../../../../../services/api";
import SyncLoader from "react-spinners/SyncLoader";
import { format } from "date-fns";
import { useAuth } from "../../../../../../hooks/auth";
import * as Dialog from "@radix-ui/react-dialog";
import { useRoutes } from "../../../../../../hooks/routes";

import IListShapeApiProps from "./dtos/IListShapeApiProps";
import ICreatorAndEditorProps from "./dtos/ICreatorAndEditorProps";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import Button from "../../../../../../shared/components/Button";
import DialogModal from "../../../../../../shared/components/DialogModal";

import { Notify } from "../../../../../../shared/utils";

import {
  Container,
  Title,
  Subtitle,
  ContainerLoading,
  Link,
  IconImg,
  ContainerButton,
} from "./styles";

const ViewShapes: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [creator, setCreator] = useState<ICreatorAndEditorProps>({ name: "" });
  const [lastEditor, setLastEditor] = useState<ICreatorAndEditorProps>({
    name: "",
  });
  const [shapeData, setShapeData] = useState<IListShapeApiProps>({
    bitsShape: {
      id: "",
      nickname: "",
      creator_id: "",
      last_editor_id: "",
      name: "",
      key_words: "",
      dpath: "",
      stroke_width: 0,
      usage_counter: 0,
      is_filled: false,
      shape_url: "",
      created_at: new Date(),
      updated_at: new Date(),
    },
    bitsShapeCategories: [],
  });

  const location = useLocation();
  const dataLocation: any = location.state;

  const { data: dataUseAuth } = useAuth();

  const { getUrl } = useRoutes();

  const userPermissionReadUser = dataUseAuth.admin_user_permissions.read_users;

  const userPermissionUpdate = dataUseAuth.admin_user_permissions.update_shapes;
  const userPermissionDelete = dataUseAuth.admin_user_permissions.delete_shapes;

  const { shape_id } = useParams();

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/view-shapes/:shape_id");
  }, []);

  useEffect(() => {
    (async () => {
      const listShapeSpecific = await api.get(`/bits-shape/${shape_id}`);

      if (userPermissionReadUser) {
        const userCreator = await api.get(
          `/profile/${listShapeSpecific.data.bitsShape.creator_id}`
        );

        const userLastEditor = await api.get(
          `/profile/${listShapeSpecific.data.bitsShape.last_editor_id}`
        );

        setCreator({
          name: `${userCreator.data.people.first_name} ${userCreator.data.people.last_name}`,
        });
        setLastEditor({
          name: `${userLastEditor.data.people.first_name} ${userLastEditor.data.people.last_name}`,
        });
      } else {
        setCreator({
          name: "Anônimo",
        });
        setLastEditor({
          name: "Anônimo",
        });
      }

      setShapeData(listShapeSpecific.data);
      setLoading(false);
    })();
  }, [shape_id, userPermissionReadUser]);

  const handleDeleteShape = useCallback(
    async (id: string) => {
      await api.delete(`/bits-shape/${id}`);
      Notify({ text: "Forma deletada com sucesso", type: "success" });
      navigate("/shapes", {
        state: { params: dataLocation?.params },
      });
    },
    [navigate, dataLocation]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Visualizar forma" />
      <MasterMain>
        {loading && (
          <ContainerLoading>
            <SyncLoader loading={loading} color="#663399" size={10} />
          </ContainerLoading>
        )}

        {!loading && (
          <Container>
            <Title>Forma:</Title>
            <IconImg src={shapeData.bitsShape.shape_url} />

            <Divider style={{ marginBottom: 20, marginTop: 20 }} />

            <Title>Nome:</Title>
            <Subtitle>
              {shapeData.bitsShape.nickname
                ? shapeData.bitsShape.nickname
                : "null"}
            </Subtitle>

            <Title>Palavras chave:</Title>
            <Subtitle>
              {shapeData.bitsShape.key_words
                ? shapeData.bitsShape.key_words
                : "null"}
            </Subtitle>

            <Title>Número de vezes usada:</Title>
            <Subtitle>{shapeData.bitsShape.usage_counter}</Subtitle>

            <Title>Espessura:</Title>
            <Subtitle>{shapeData.bitsShape.stroke_width}</Subtitle>

            <Title>Forma preenchida:</Title>
            <Subtitle>{shapeData.bitsShape.is_filled ? "Sim" : "Não"}</Subtitle>

            <Title>Forma URL:</Title>
            <Link href={shapeData.bitsShape.shape_url} target="_blank">
              {shapeData.bitsShape.shape_url}
            </Link>

            <Title>DPATH:</Title>
            <Subtitle>
              {shapeData.bitsShape.dpath ? shapeData.bitsShape.dpath : "null"}
            </Subtitle>

            <Title>Criador:</Title>
            <Subtitle>{creator.name}</Subtitle>

            <Title>Último a editar:</Title>
            <Subtitle>{lastEditor.name}</Subtitle>

            <Title>Criado em:</Title>
            <Subtitle>
              {`${new Date(shapeData.bitsShape.created_at)}`
                ? `${format(
                    new Date(shapeData.bitsShape.created_at),
                    "dd/MM/yyyy HH:mm"
                  )}`
                : "null"}
            </Subtitle>

            <Title>Última atualização:</Title>
            <Subtitle>
              {`${new Date(shapeData.bitsShape.updated_at)}`
                ? `${format(
                    new Date(shapeData.bitsShape.updated_at),
                    "dd/MM/yyyy HH:mm"
                  )}`
                : "null"}
            </Subtitle>

            <Divider style={{ marginBottom: 20 }} />

            <Title>Categorias:</Title>
            {shapeData.bitsShapeCategories.map((item) => (
              <Subtitle key={item.id} style={{ marginBottom: 0, marginTop: 5 }}>
                {item.title}
              </Subtitle>
            ))}

            <Divider style={{ marginBottom: 20, marginTop: 20 }} />

            <ContainerButton>
              <Button
                style={{ width: 100 }}
                onClick={() =>
                  navigate("/shapes", {
                    state: { params: dataLocation?.params },
                  })
                }
              >
                Voltar
              </Button>

              <Button
                style={{ width: 100, marginLeft: 20 }}
                onClick={() =>
                  navigate(`/edit-shapes/${shape_id}`, {
                    state: { params: dataLocation?.params },
                  })
                }
                disabled={!userPermissionUpdate}
              >
                Editar
              </Button>

              <Dialog.Root>
                <Dialog.Trigger asChild>
                  <Button
                    style={{ width: 100, marginLeft: 20 }}
                    disabled={!userPermissionDelete}
                  >
                    Excluir
                  </Button>
                </Dialog.Trigger>

                <DialogModal
                  title="Deletar forma"
                  type="delete"
                  subtitle={shapeData.bitsShape.nickname}
                  callback={() => handleDeleteShape(shapeData.bitsShape.id)}
                />
              </Dialog.Root>
            </ContainerButton>
          </Container>
        )}
      </MasterMain>
    </MasterContainer>
  );
};

export default ViewShapes;
